/**
 * Copyright schukai GmbH and contributors 2023. All Rights Reserved.
 * Node module: @schukai/monster
 * This file is licensed under the AGPLv3 License.
 * License text available at https://www.gnu.org/licenses/agpl-3.0.en.html
 */

import { Base } from "../types/base.mjs";
import { getGlobalObject } from "../types/global.mjs";
import { validateString } from "../types/validate.mjs";
import { ATTRIBUTE_THEME_NAME, DEFAULT_THEME } from "./constants.mjs";
import { instanceSymbol } from "../constants.mjs";
export { Theme, getDocumentTheme };

/**
 * The Theme class provides the functionality for the theme.
 *
 * @externalExample ../../example/dom/theme.mjs
 * @license AGPLv3
 * @since 1.7.0
 * @copyright schukai GmbH
 * @memberOf Monster.DOM
 * @summary A theme class
 */
class Theme extends Base {
	/**
	 *
	 * @param name
	 * @throws {TypeError} value is not a string
	 */
	constructor(name) {
		super();
		validateString(name);
		this.name = name;
	}

	/**
	 * This method is called by the `instanceof` operator.
	 * @returns {symbol}
	 * @since 2.1.0
	 */
	static get [instanceSymbol]() {
		return Symbol.for("@schukai/monster/dom/theme");
	}

	/**
	 *
	 * @returns {string}
	 */
	getName() {
		return this.name;
	}
}

/**
 * The theming used in the document can be defined via the html-tag.
 * The theming is specified via the attribute `data-monster-theme-name`.
 *
 * As name for a theme all characters are valid, which are also allowed for a HTMLElement-ID.
 *
 * ```
 * <html data-monster-theme-name="my-theme">
 * ```
 *
 * the default theme name is `monster`.
 *
 * @return {Theme}
 * @memberOf Monster.DOM
 * @license AGPLv3
 * @since 1.7.0
 */
function getDocumentTheme() {
	const document = getGlobalObject("document");
	let name = DEFAULT_THEME;

	const element = document.querySelector("html");
	if (element instanceof HTMLElement) {
		const theme = element.getAttribute(ATTRIBUTE_THEME_NAME);
		if (theme) {
			name = theme;
		}
	}

	return new Theme(name);
}
